<script setup lang="ts">
/** Appoint / Withdraw MD (B32115) — 문서 패키지(DR 1부, 단일 스테이지). */
import { ref, computed } from 'vue';
import AppointWithdrawMdDrDoc from './AppointWithdrawMdDrDoc.vue';
import { MD_ACTION_OPTIONS } from '../../chatflows/appointWithdrawMdFlow';

/** 결의 종류 — appoint(선임) / withdraw(해임) / both(선임+해임, 한 부에 결의 2개). */
const props = withDefaults(defineProps<{ action?: string }>(), { action: 'both' });
const emit = defineEmits<{ (e: 'update:action', v: string): void }>();
const showAppointment = computed(() => props.action !== 'withdraw');
const showResignation = computed(() => props.action !== 'appoint');
const docName = computed(() => (props.action === 'appoint' ? 'Appointment of MD' : props.action === 'withdraw' ? 'Resignation of MD' : 'Appointment & Resignation of MD'));

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }
</script>

<template>
  <div class="md-pkg">
    <div class="zoom-bar">
      <div class="act-switch" role="group" aria-label="Resolution">
        <button
          v-for="o in MD_ACTION_OPTIONS"
          :key="o.key"
          type="button"
          class="act-btn"
          :class="{ on: action === o.key }"
          :title="o.desc"
          @click="emit('update:action', o.key)"
        >{{ o.label }}</button>
      </div>
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>

    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <div class="pkg-doclabel">
        <span class="ic">📝</span><span class="nm">Directors' Resolution ({{ docName }})</span>
        <span class="no">Doc 1 / 1</span>
      </div>
      <div class="simple-frame">
        <AppointWithdrawMdDrDoc :show-appointment="showAppointment" :show-resignation="showResignation" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.md-pkg { color: #111; }
.zoom-bar { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; }
.doc-zoom-btn { min-width: 36px; font-size: 15px; }
.doc-zoom-btn:disabled { opacity: 0.35; cursor: default; }
.doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.act-switch { display: inline-flex; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; flex-wrap: wrap; }
.act-btn { border: none; background: transparent; cursor: pointer; font-size: 12px; font-weight: 700; color: #4b5563; border-radius: 8px; padding: 0 12px; height: 32px; }
.act-btn.on { background: #fff; color: #1b64da; box-shadow: 0 1px 3px rgba(20,30,60,.12); }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; }
.pkg-doclabel .ic { font-size: 14px; }
.pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
</style>
